import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { CatalogManager } from "@/components/admin/CatalogManager";
import { UsersManager } from "@/components/admin/UsersManager";
import { SiteSettings } from "@/components/admin/SiteSettings";
import { Loader2, Ticket, CheckCircle2, Clock, DollarSign, Users, Inbox, Mail, MessageCircle, Download } from "lucide-react";
import { toast } from "sonner";

const isStaff = (u) => u && (u.role === "admin" || u.role === "staff");

export default function Admin() {
  const { t, money, user, authReady } = useApp();
  const nav = useNavigate();
  const a = t.admin;
  const isAdmin = user?.role === "admin";
  const TABS = isAdmin ? ["bookings", "requests", "reviews", "subscribers", "catalog", "site", "users"] : ["bookings", "requests", "reviews", "subscribers"];
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  const tabRef = useRef(tab);

  useEffect(() => {
    if (authReady && !isStaff(user)) nav("/dashboard");
  }, [user, authReady, nav]);

  const load = useCallback(() => {
    if (!isStaff(user)) return;
    const current = tab;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    const ep = { bookings: "/admin/bookings", requests: "/admin/service-requests", reviews: "/admin/reviews", subscribers: "/admin/subscribers", users: "/admin/users" }[current];
    if (!ep) { setRows([]); return; }
    setRows(null);
    api.get(ep).then((r) => { if (tabRef.current === current) setRows(r.data); })
      .catch((e) => { if (tabRef.current === current) { toast.error(apiError(e.response?.data?.detail)); setRows([]); } });
  }, [tab, user]);

  useEffect(() => { tabRef.current = tab; }, [tab]);
  useEffect(load, [load]);

  const notify = (n) => {
    if (!n) return;
    const parts = [a.updated, n.email_sent ? a.emailSent : null].filter(Boolean).join(" · ");
    if (n.whatsapp_link) {
      toast.success(parts, { duration: 10000, action: { label: a.sendWhatsapp, onClick: () => window.open(n.whatsapp_link, "_blank", "noopener,noreferrer") } });
    } else toast.success(parts);
  };
  const patch = async (path, status) => {
    try { const { data } = await api.patch(path, { status }); notify(data.notification); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resend = async (ref) => {
    try { await api.post(`/admin/bookings/${ref}/resend-email`); toast.success(a.emailSent); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const unsubscribe = async (id) => {
    try { await api.delete(`/admin/subscribers/${id}`); toast.success(a.updated); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const link = document.createElement("a"); link.href = url; link.download = "subscribers.csv"; link.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const waLink = (phone, text) => {
    const d = (phone || "").replace(/\D/g, "");
    return d ? `https://wa.me/${d.startsWith("0") ? "964" + d.slice(1) : d}?text=${encodeURIComponent(text)}` : null;
  };

  const cards = stats ? [
    { Icon: Ticket, label: a.totalBookings, value: stats.bookings },
    { Icon: CheckCircle2, label: a.confirmed, value: stats.confirmed },
    { Icon: Clock, label: a.pending, value: stats.pending },
    { Icon: DollarSign, label: a.revenue, value: money(stats.revenue_paid) },
    { Icon: Users, label: a.users, value: stats.users },
    { Icon: Inbox, label: a.newRequests, value: stats.service_requests },
  ] : [];
  const selCls = "text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white";
  const th = (cols) => cols.map((h, i) => <th key={i} className="px-4 py-3 text-start font-semibold">{h}</th>);

  return (
    <div className="bg-sand min-h-screen" data-testid="admin-page">
      <div className="bg-tigris-dark py-10 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex items-end justify-between gap-4">
          <div>
            <p className="text-gold text-xs font-bold mb-1">{t.brand}</p>
            <h1 className="text-white text-3xl font-bold font-display">{a.title}</h1>
          </div>
          {user && <span data-testid="admin-role-badge" className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/10 text-white">{a.roles[user.role]} · {user.name}</span>}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6 pb-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {cards.map(({ Icon, label, value }) => (
            <div key={label} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-testid={`stat-${label}`}>
              <Icon size={18} className="text-gold mb-2" />
              <div className="text-xl font-extrabold text-tigris">{value}</div>
              <div className="text-[11px] text-slate-500 font-semibold">{label}</div>
            </div>
          ))}
        </div>

        {tab === "subscribers" && (
          <div className="flex justify-end mb-3">
            <button data-testid="export-subscribers-btn" onClick={exportCsv} className="btn-primary px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5"><Download size={14} /> {a.exportCsv}</button>
          </div>
        )}
        <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
          {TABS.map((k) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-colors ${tab === k ? "bg-tigris text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
              {a.tabs[k]}
            </button>
          ))}
        </div>

        {tab === "catalog" && isAdmin ? <CatalogManager /> :
        tab === "site" && isAdmin ? <SiteSettings /> :
        tab === "users" && isAdmin ? (rows === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-tigris" size={36} /></div> : <UsersManager rows={rows} reload={load} />) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
          {rows === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-tigris" size={36} /></div>
          : rows.length === 0 ? <p className="text-center text-slate-400 py-16">{a.empty}</p>
          : (
            <table className="w-full text-sm" data-testid={`admin-table-${tab}`}>
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  {tab === "bookings" && th([a.ref, a.customer, a.service, a.date, a.amount, a.status, ""])}
                  {tab === "requests" && th([a.ref, a.customer, a.type, a.date, a.notes, a.status, ""])}
                  {tab === "reviews" && th([a.customer, a.service, a.rating, a.notes, a.date, a.status])}
                  {tab === "subscribers" && th([a.email, "WhatsApp", a.name, a.date, a.status, ""])}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tab === "bookings" && rows.map((b) => (
                  <tr key={b.ref} data-testid={`admin-booking-${b.ref}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-mono text-tigris font-semibold">{b.ref}</td>
                    <td className="px-4 py-3"><div className="font-semibold">{b.contact_name}</div><div className="text-xs text-slate-400" dir="ltr">{b.contact_phone}{b.contact_email ? ` · ${b.contact_email}` : ""}</div></td>
                    <td className="px-4 py-3 max-w-[260px] truncate">{b.title}</td>
                    <td className="px-4 py-3 text-xs">{b.start_date || b.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 font-bold text-tigris">{money(b.amount)}</td>
                    <td className="px-4 py-3">
                      <select data-testid={`admin-status-${b.ref}`} className={selCls} value={b.status} onChange={(e) => patch(`/admin/bookings/${b.ref}`, e.target.value)}>
                        <option value="pending_payment">{a.pendingOpt}</option>
                        <option value="confirmed">{a.confirmed}</option>
                        <option value="cancelled">{a.cancelled}</option>
                      </select>
                      <div className="text-[10px] text-slate-400 mt-1">{b.payment_status}{b.updated_by ? ` · ${b.updated_by}` : ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {b.contact_email && (
                          <button data-testid={`admin-resend-${b.ref}`} onClick={() => resend(b.ref)} title={a.resend}
                            className={`p-2 rounded-lg hover:bg-tigris/10 ${b.email_sent ? "text-green-600" : "text-slate-400"}`}><Mail size={16} /></button>
                        )}
                        {waLink(b.contact_phone) && (
                          <a data-testid={`admin-wa-${b.ref}`} href={waLink(b.contact_phone, `${a.waIntro} ${b.ref}`)} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-green-50 text-[#25D366]"><MessageCircle size={16} /></a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {tab === "requests" && rows.map((r) => (
                  <tr key={r.ref} data-testid={`admin-request-${r.ref}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-mono text-tigris font-semibold">{r.ref}</td>
                    <td className="px-4 py-3"><div className="font-semibold">{r.name}</div><div className="text-xs text-slate-400" dir="ltr">{r.phone}{r.email ? ` · ${r.email}` : ""}</div></td>
                    <td className="px-4 py-3">{t.services.types[r.service]}{r.destination ? ` · ${r.destination}` : ""}</td>
                    <td className="px-4 py-3 text-xs">{r.travel_date || r.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-xs max-w-[220px] truncate">{r.notes}</td>
                    <td className="px-4 py-3">
                      <select data-testid={`admin-req-status-${r.ref}`} className={selCls} value={r.status} onChange={(e) => patch(`/admin/service-requests/${r.ref}`, e.target.value)}>
                        {["new", "in_progress", "done", "cancelled"].map((s) => <option key={s} value={s}>{a.reqStatus[s]}</option>)}
                      </select>
                      {r.updated_by && <div className="text-[10px] text-slate-400 mt-1">{r.updated_by}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {waLink(r.phone) && <a data-testid={`admin-req-wa-${r.ref}`} href={waLink(r.phone, `${a.waIntro} ${r.ref}`)} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-green-50 text-[#25D366] inline-flex"><MessageCircle size={16} /></a>}
                    </td>
                  </tr>
                ))}
                {tab === "subscribers" && rows.map((sb) => (
                  <tr key={sb.id} data-testid={`admin-subscriber-${sb.id}`} className={`hover:bg-slate-50/60 ${sb.active === false ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3" dir="ltr">{sb.email || "—"}</td>
                    <td className="px-4 py-3" dir="ltr">{sb.whatsapp ? <a href={`https://wa.me/${sb.whatsapp}`} target="_blank" rel="noreferrer" className="text-[#25D366] font-semibold">+{sb.whatsapp}</a> : "—"}</td>
                    <td className="px-4 py-3">{sb.name || "—"}</td>
                    <td className="px-4 py-3 text-xs">{sb.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-1 rounded-full ${sb.active === false ? "bg-slate-100 text-slate-500" : "bg-green-100 text-green-700"}`}>{sb.active === false ? a.unsubscribed : a.subscribed}</span></td>
                    <td className="px-4 py-3">{sb.active !== false && <button data-testid={`admin-unsub-${sb.id}`} onClick={() => unsubscribe(sb.id)} className="text-xs text-red-500 hover:underline">{a.unsubscribe}</button>}</td>
                  </tr>
                ))}
                {tab === "reviews" && rows.map((rv) => (
                  <tr key={rv.id} data-testid={`admin-review-${rv.id}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-semibold">{rv.user_name}<div className="text-[10px] text-slate-400 font-mono">{rv.booking_ref}</div></td>
                    <td className="px-4 py-3 text-xs">{rv.type} · {rv.item_id}</td>
                    <td className="px-4 py-3 font-bold text-gold">{"★".repeat(rv.rating)}</td>
                    <td className="px-4 py-3 text-xs max-w-[320px]">{rv.comment}</td>
                    <td className="px-4 py-3 text-xs">{rv.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <select data-testid={`admin-review-status-${rv.id}`} className={selCls} value={rv.status} onChange={(e) => patch(`/admin/reviews/${rv.id}`, e.target.value)}>
                        <option value="published">{a.published}</option>
                        <option value="hidden">{a.hidden}</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
